import { X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface PinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (pin: string) => void;
  titleName?: string;
  error?: string | null;
  isLoading?: boolean;
}

export function PinDialog({
  open,
  onOpenChange,
  onSubmit,
  titleName = "Transactions",
  error,
  isLoading,
}: PinDialogProps) {
  const { t } = useTranslation();
  const [pin, setPin] = useState<string[]>(Array(4).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset pin when dialog opens
  useEffect(() => {
    if (open) {
      setPin(Array(4).fill(""));
      // Focus first input after dialog opens
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [open]);

  const handlePinChange = useCallback((index: number, value: string) => {
    // Only handle paste (multiple digits)
    // Single digit input is handled by onKeyDown with e.preventDefault()
    if (value.length > 1) {
      const digits = value
        .replace(/\D/g, "")
        .split("")
        .slice(0, 4 - index);
      if (digits.length === 0) return;

      setPin((prev) => {
        const newPin = [...prev];
        digits.forEach((digit, i) => {
          if (index + i < 4) {
            newPin[index + i] = digit;
          }
        });
        return newPin;
      });
      // Focus the next empty input or last input
      const nextIndex = Math.min(index + digits.length, 3);
      inputRefs.current[nextIndex]?.focus();
    }
  }, []);

  const handlePinKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle number keys (0-9) - replace current value and move to next
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        setPin((prev) => {
          const newPin = [...prev];
          newPin[index] = e.key;
          return newPin;
        });
        // Auto focus next input
        if (index < 3) {
          inputRefs.current[index + 1]?.focus();
        }
        return;
      }

      if (e.key === "Backspace") {
        if (pin[index]) {
          // Clear current input
          setPin((prev) => {
            const newPin = [...prev];
            newPin[index] = "";
            return newPin;
          });
        } else if (index > 0) {
          // Focus previous input if current is empty
          inputRefs.current[index - 1]?.focus();
        }
        e.preventDefault();
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
        e.preventDefault();
      } else if (e.key === "ArrowRight" && index < 3) {
        inputRefs.current[index + 1]?.focus();
        e.preventDefault();
      }
    },
    [pin],
  );

  const handleSubmit = useCallback(() => {
    const fullPin = pin.join("");
    if (fullPin.length === 4) {
      onSubmit(fullPin);
    }
  }, [pin, onSubmit]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[361px] h-[350px] p-0 bg-white backdrop-blur-[24px] rounded-[20px] border-black/[0.03] shadow-[0px_1px_1px_-0.5px_rgba(0,0,0,0.03),0px_3px_3px_-1.5px_rgba(0,0,0,0.03),0px_20px_20px_-12px_rgba(0,0,0,0.03),0px_32px_32px_-16px_rgba(0,0,0,0.03)] overflow-hidden gap-0 [&>button]:hidden text-textHighEm"
        data-node-id="pin-dialog"
        aria-describedby={undefined}
      >
        {/* Modal Header */}
        <DialogHeader className="h-[56px] flex flex-row items-center justify-between px-[20px] py-[16px] relative space-y-0">
          <div className="flex-1" />
          <DialogTitle className="text-[16px] font-semibold text-textHighEm leading-[24px] text-center absolute left-1/2 -translate-x-1/2 w-full max-w-[calc(100%-80px)]">
            {t(
              "use_points.pin_dialog.enter_to_access",
              "Enter PIN to access {{titleName}}",
              { titleName },
            )}
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="absolute right-[12px] top-[12px] w-[32px] h-[32px] flex items-center justify-center bg-neutral100 border border-surfaceSpecialLightS3DarkS4 rounded-[8px] hover:bg-gray-200 transition-colors z-10 text-textMedEm"
          >
            <X size={18} />
          </Button>
        </DialogHeader>

        {/* Modal Content */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="p-[24px_20px] flex flex-col h-full items-center justify-between"
        >
          {/* PIN Input Group */}
          <div className="w-full flex flex-col gap-[12px]">
            <label className="text-[14px] font-medium text-textHighEm text-center">
              {t("use_points.pin_dialog.pin_label", "Enter your PIN")}
            </label>
            <div className="flex justify-between gap-[8px]">
              {pin.map((digit: string, idx: number) => (
                <Input
                  key={idx}
                  ref={(el) => {
                    inputRefs.current[idx] = el;
                  }}
                  id={"pin-input-" + idx}
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  placeholder="-"
                  type="password"
                  onChange={(e) => handlePinChange(idx, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(idx, e)}
                  className="w-full h-[52px] max-w-[65.8px] rounded-[12px] text-center text-[22px] font-medium border-surfaceSpecialLightS3DarkS4 focus-visible:ring-surfacePrimaryHighEm px-0"
                />
              ))}
            </div>
            {error && (
              <span className="text-[12px] text-destructive text-center font-medium animate-in fade-in slide-in-from-top-1">
                {error}
              </span>
            )}
          </div>

          {/* Action Button */}
          <div className="w-full mt-[40px]">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-[40px] bg-surfacePrimaryMedEm hover:bg-surfacePrimaryHighEm text-white rounded-[10px] text-[14px] font-medium flex items-center justify-center shadow-none border-none disabled:opacity-50"
            >
              {isLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                t("common.next", "Next")
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
